import { Paper } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import { IsAllowed } from "../../acl";
import { buildingFilesCacheKey, useCheckFloorPlanAllFilesStatus } from "../../api/hooks/allHooks";
import apiUrls from "../../api/urls";
import { eventNames, trackGAEvent, withGAEventTracking } from "../../functions/googleAnalytics";
import { customApiCall } from "../../utils/axios";
import { ComponentsHeaderWithDownloadUploadAction } from "../ComponentsCommonHeader";
import { BuildingProfileTable } from "./BuildingProfileTable";
import {
  BuildingProfileTableFilesSectionSelection,
  BuildingProfileTableTabs,
  useBuildingProfileTableFilesSectionSelection,
} from "./BuildingProfileTableTabs";

export const BuildingProfileFiles = ({ buildingFiles, className, isLoading, projectId, tableClassName }) => {
  const { t } = useTranslation();
  const { data: filesStatus } = useCheckFloorPlanAllFilesStatus(projectId);
  const queryClient = useQueryClient();

  const downloadBuildingPassportFiles = useCallback(() => {
    const url = apiUrls.layerFiles.buildingPassport.download(projectId);
    const fileName = "Floor plan.zip";
    const errorMessage = t("general.downloadFileProblem");
    customApiCall.download({ errorMessage, fileName, url });
  }, [projectId, t]);

  const fileMetaDataAPI = {
    getFileMetaDataBody: (file) => ({
      name: file?.name,
      project: projectId,
      section: selectedFileSection,
      type: file?.name?.split(".")?.pop() ?? file?.type,
    }),
    url: apiUrls.layerFiles.buildingPassport.uploadFileMetaData,
  };

  const { selectedFileSection, selectedTab, setSelectedFileSection, setSelectedTab } =
    useBuildingProfileTableFilesSectionSelection();

  return (
    <Paper className={`${className}   relative min-h-[20rem]`}>
      <ComponentsHeaderWithDownloadUploadAction
        downloadAction={() =>
          withGAEventTracking(eventNames.BP_DOWNLOAD_ALL_FLOOR_PLANS, {}, () =>
            downloadBuildingPassportFiles(projectId)
          )
        }
        downloadActionIsDisabled={filesStatus?.status !== 200}
        downloadActionText={t("BuildingProfile.table.downloadAllFiles")}
        hasDownloadAccess={IsAllowed("BUILDING_PROFILE.DOWNLOAD_FILES")}
        hasUploadAccess={IsAllowed("BUILDING_PROFILE.UPLOAD_FILES")}
        onUploadClick={() => trackGAEvent(eventNames.BP_UPLOAD_FLOOR_PLAN)}
        onUploadDone={() => {
          queryClient.invalidateQueries(buildingFilesCacheKey(projectId));
        }}
        title={t("BuildingProfile.table.title")}
        uploadFileMetaDataAPI={fileMetaDataAPI}
        uploadFormDataFileKeyName="layer_file"
        uploadQueryParams={(fileMetaData) => [{ name: "floors_layer", value: fileMetaData?.id }]}
        uploadRenderModalBody={() => (
          <BuildingProfileTableFilesSectionSelection
            selectedFileSection={selectedFileSection}
            setSelectedFileSection={setSelectedFileSection}
          />
        )}
        uploadUrl={apiUrls.layerFiles.buildingPassport.uploadFile}
      />
      <BuildingProfileTableTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
      <BuildingProfileTable
        buildingFiles={buildingFiles}
        isLoading={isLoading}
        projectId={projectId}
        selectedTab={selectedTab}
        tableClassName={tableClassName}
      />
    </Paper>
  );
};
